import { Select } from 'antd';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useAuthStore from '../../../../store/authStore';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { IMarker } from '../../../../types/marker';
import { UserGroupPermissions } from '../../../../types/userGroupPermissions';
import { TabKey } from '../../../../utils/common';
import { getDateRange } from '../../../../utils/dateRange';
import { useIsDwf, useIsTnw } from '../../../../utils/hooks';
import { DatasetChangeWarningModal } from '../../ResultPanel/component/Modal/DatasetWarning/DatasetChangeWarningModal';
import { useAdvancedAnalytics } from '../hooks/useAdvanceAnalytics';
import { useAnalyticsCommonInput } from '../hooks/useAnalyticsCommonInput';
import { useAnalyticsNavigation } from '../hooks/useAnalyticsNavigation';
import { useBasicAnalyticsData } from '../hooks/useBasicAnalyticsData';
import { useRosePlot } from '../hooks/useRosePlot';
import { useScatterPlot } from '../hooks/useScatterPlot';
import { useScatterTable } from '../hooks/useScatterTable';
import { useTimeSeriesPlot } from '../hooks/useTimeSeriesPlot';
import { AnalyticsSpinner } from './AnalyticsSpinner';
import AnalyticsLayout from './Layout/AnalyticsLayout';
import ListNavigation from './ListNavigation';
import ExceedanceBox from './Wrapper/ExceedanceBox';
import ExtremeValuesBox from './Wrapper/ExtremeValuesBox';
import HistogramBox from './Wrapper/HistogramBox';
import PersistenceBox from './Wrapper/PersistenceBox';
import RosePlotBox from './Wrapper/RosePlotBox';
import ScatterPlotBox from './Wrapper/ScatterPlotBox';
import ScatterTableBox from './Wrapper/ScatterTableBox';
import StatisticsBox from './Wrapper/StatisticsBox';
import TidalLevelBox from './Wrapper/TidalLevelBox';
import TSPlotBox from './Wrapper/TSPlotBox';

type AnalyticsDeps = {
  basicData: ReturnType<typeof useBasicAnalyticsData>;
  tsPlot: ReturnType<typeof useTimeSeriesPlot>;
  rosePlot: ReturnType<typeof useRosePlot>;
  scatterPlot: ReturnType<typeof useScatterPlot>;
  advancedAnalytics: ReturnType<typeof useAdvancedAnalytics>;
  scatterTable: ReturnType<typeof useScatterTable>;
  commonInput: ReturnType<typeof useAnalyticsCommonInput>;
  navigation: ReturnType<typeof useAnalyticsNavigation>;
  isFwe?: boolean;
};

export default function AnalyticsPanel(props: {
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointProps: string;
  pointName: string;
  gridCellSize: string;
  waterDepth: string;
  point?: IMarker;
  category: string | string[];
  userInput: IAnalytcisUserInput;
  updateUserInput: (update: Partial<IAnalytcisUserInput>) => void;
  onChangeActiveKey: (item: any, key: TabKey) => void;
  analyticsDeps: AnalyticsDeps;
  isForAllAnalytics: boolean;
  analyticsDateTime: any[];
  setIsForAllAnalytics: (value: boolean) => void;
  setAnalyticsDateTime: (value: any[]) => void;
}) {
  const {
    point,
    userInput,
    updateUserInput,
    analyticsDeps,
    isForAllAnalytics,
    setIsForAllAnalytics,
    analyticsDateTime,
    setAnalyticsDateTime,
  } = props;

  const enrichedMetadata = useAnalyticsStore((state) => state.enrichedMetadata);
  const user = useAuthStore((state) => state.user);
  const isOfflineDataset =
    point?.isDatasetOffline?.[props.category as string] ?? true;

  const isTnw = useIsTnw(props.previewDatasetId) && user.authenticated;
  const isDwf = useIsDwf(props.previewDatasetId) && user.authenticated;

  const {
    basicData,
    tsPlot,
    rosePlot,
    scatterPlot,
    advancedAnalytics,
    scatterTable,
    commonInput: commonInputAnalytics,
    navigation: analyticsNavigation,
  } = analyticsDeps;

  const {
    timeSeriesPreview,
    timeSeriesPreviewError,
    scatterPlotPreview,
    scatterPlotPreviewError,
    loadingInitialAnalytics,
    defaultRosePlotDirection,
    rosePlotPreview,
    rosePlotPreviewError,
  } = basicData;

  if (loadingInitialAnalytics) {
    return <AnalyticsSpinner />;
  }

  const isUpdating =
    advancedAnalytics.isUpdating ||
    tsPlot.loadingUpdateTS ||
    rosePlot.loadingRosePlot ||
    scatterPlot.loadingScatterPlot ||
    scatterTable.isUpdatingScatterTable;

  return (
    <>
      <DatasetChangeWarningModal
        visible={commonInputAnalytics.isChangeDatasetWarningVisible}
        onOk={commonInputAnalytics.handleChangeDatasetWarningOk}
        onCancel={commonInputAnalytics.handleChangeDatasetWarningCancel}
      />
      <AnalyticsLayout
        globalContent={
          <div style={{ padding: '8px', paddingTop: 0 }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span>Point</span>
            </div>
            <Select
              id='Analytics-Select_Change_Point'
              style={{ width: '100%' }}
              placeholder='Select point'
              onChange={commonInputAnalytics.handleChangePoint}
              options={commonInputAnalytics.pointOptions}
              value={point}
              disabled={loadingInitialAnalytics || isUpdating}
            />
          </div>
        }
        navigationContent={
          <ListNavigation
            availableAnalytics={analyticsNavigation.availableAnalytics}
            activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
            handleAnalyticsTypeChanged={
              analyticsNavigation.handleAnalyticsTypeChanged
            }
          />
        }
      >
        <TSPlotBox
          timeSeriesPreview={timeSeriesPreview}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          tsPreviewError={timeSeriesPreviewError}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          previewDatasetId={props.previewDatasetId}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          previewDatasetTimeRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          categoryDataset={commonInputAnalytics.categoryDataset}
          onUpdateTS={tsPlot.onUpdateTS}
          updatedTSData={tsPlot.updatedTSData}
          pointProps={props.pointProps}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          errorUpdate={tsPlot.errorTs}
          loadingButtonUpdate={tsPlot.loadingUpdateTS}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isOffline={isOfflineDataset}
          coordinates={props.coordinates}
          enrichedMetadata={enrichedMetadata}
        />
        <RosePlotBox
          rosePlotPreview={rosePlotPreview}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          rosePlotPreviewError={rosePlotPreviewError}
          previewDatasetId={props.previewDatasetId}
          userGroupPermissionDateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          categoryDataset={commonInputAnalytics.categoryDataset}
          defaultRosePlotDirection={defaultRosePlotDirection}
          pointProps={props.pointProps}
          pointName={props.pointName}
          point={point}
          userInput={userInput}
          updateUserInput={updateUserInput}
          errorRosePlot={rosePlot.errorRosePlot}
          loadingRosePlot={rosePlot.loadingRosePlot}
          handleChangePoint={commonInputAnalytics.handleChangePoint}
          pointOptions={commonInputAnalytics.pointOptions}
          updateRosePlotData={rosePlot.updateRosePlotData}
          setUpdatedRosePlotData={rosePlot.setUpdatedRosePlotData}
          onUpdateRosePlot={rosePlot.onUpdateRosePlot}
          isOffline={isOfflineDataset}
          enrichedMetadata={enrichedMetadata}
          coordinates={props.coordinates}
        />
        <ScatterPlotBox
          enrichedMetadata={enrichedMetadata}
          coordinates={props.coordinates}
          previewDatasetId={props.previewDatasetId}
          previewDatasetTimeRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          categoryDataset={commonInputAnalytics.categoryDataset}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          onUpdateScatterPlot={scatterPlot.onUpdateScatterPlot}
          updatedScatterPlotData={scatterPlot.updatedScatterPlotData}
          onUpdateJointScatterPlot={scatterPlot.onUpdateJointScatterPlot}
          pointProps={props.pointProps}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          handleChangePoint={commonInputAnalytics.handleChangePoint}
          errorScatterPlot={scatterPlot.errorScatterPlot}
          loadingScatterPlot={scatterPlot.loadingScatterPlot}
          pointOptions={commonInputAnalytics.pointOptions}
          scatterPlotPreview={scatterPlotPreview}
          scatterPlotPreviewError={scatterPlotPreviewError}
          isOffline={isOfflineDataset}
        />
        <ScatterTableBox
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          categoryDataset={commonInputAnalytics.categoryDataset}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          pointName={props.pointName}
          enrichedMetadata={enrichedMetadata}
          userInput={userInput}
          updateUserInput={updateUserInput}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          point={point}
          scatterTableData={scatterTable.scatterTableData}
          scatterTableLinks={scatterTable.scatterTableLinks}
          scatterTableError={scatterTable.scatterTableError}
          updatingScatterTable={scatterTable.isUpdatingScatterTable}
          onUpdateScatterTable={scatterTable.updateScatterTable}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          isOfflineDataset={isOfflineDataset}
        />
        <PersistenceBox
          enrichedMetadata={enrichedMetadata}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          isUpdating={advancedAnalytics.isUpdating}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          categoryDataset={commonInputAnalytics.categoryDataset}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          onUpdate={advancedAnalytics.updatePersistence}
          persistenceImage={advancedAnalytics.images.persistence}
          error={advancedAnalytics.errors.persistence}
          persistenceLinks={advancedAnalytics.links.persistence}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          point={point}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          isOfflineDataset={isOfflineDataset}
        />
        <ExtremeValuesBox
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={commonInputAnalytics.categoryDataset}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          enrichedMetadata={enrichedMetadata}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          onUpdate={advancedAnalytics.updateExtremeValues}
          extremeValuesImage={advancedAnalytics.images.extremeValues}
          extremeValuesLinks={advancedAnalytics.links.extremeValues}
          error={advancedAnalytics.errors.extremeValues}
          updating={advancedAnalytics.isUpdating}
          isOfflineDataset={isOfflineDataset}
        />
        <HistogramBox
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={commonInputAnalytics.categoryDataset}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          enrichedMetadata={enrichedMetadata}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          point={point}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          onUpdate={advancedAnalytics.updateHistogram}
          histogramImage={advancedAnalytics.images.histogram}
          histogramLinks={advancedAnalytics.links.histogram}
          error={advancedAnalytics.errors.histogram}
          updating={advancedAnalytics.isUpdating}
          isOfflineDataset={isOfflineDataset}
          waterDepth={props.waterDepth}
          gridCellSize={props.gridCellSize}
        />
        <TidalLevelBox
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={commonInputAnalytics.categoryDataset}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          enrichedMetadata={enrichedMetadata}
          updating={advancedAnalytics.isUpdating}
          onUpdate={advancedAnalytics.updateTidalLevel}
          tidalLevelValueImage={advancedAnalytics.images.tidal}
          tidalValueLinks={advancedAnalytics.links.tidal}
          error={advancedAnalytics.errors.tidal}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          point={point}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          isOfflineDataset={isOfflineDataset}
        />
        <StatisticsBox
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={commonInputAnalytics.categoryDataset}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          enrichedMetadata={enrichedMetadata}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          point={point}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          onUpdate={advancedAnalytics.updateStatistics}
          statisticsImages={advancedAnalytics.images.statistics}
          statisticsValuesLinks={advancedAnalytics.links.statistics}
          error={advancedAnalytics.errors.statistics}
          updating={advancedAnalytics.isUpdating}
          isOfflineDataset={isOfflineDataset}
        />
        <ExceedanceBox
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={commonInputAnalytics.categoryDataset}
          dateRange={getDateRange(
            props.previewDatasetId,
            props.userGroupPermissions,
            isTnw,
            isDwf,
          )}
          enrichedMetadata={enrichedMetadata}
          updating={advancedAnalytics.isUpdating}
          onUpdate={advancedAnalytics.updateExceedance}
          exceedanceValuesImage={advancedAnalytics.images.exceedance}
          exceedanceValuesLinks={advancedAnalytics.links.exceedance}
          error={advancedAnalytics.errors.exceedance}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={commonInputAnalytics.onDatasetChange}
          point={point}
          onChangeActiveKey={commonInputAnalytics.handleChangePoint}
          pointOption={commonInputAnalytics.pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          isOfflineDataset={isOfflineDataset}
        />
      </AnalyticsLayout>
    </>
  );
}
